import CloseIcon from '@mui/icons-material/Close';
import { Dialog, DialogContent, Grid2 as Grid, Hidden, IconButton, Typography } from '@mui/material';
import { useMediaQuery, useTheme } from '@mui/material';
import MuiDialogTitle from '@mui/material/DialogTitle';
import { map, sum } from 'lodash';
import moment from 'moment';
import React, { useContext, useState, useEffect } from 'react';
import TagManager from 'react-gtm-module';
import { useNavigate } from 'react-router-dom';
import useScript from 'react-script-hook';
import Swal from 'sweetalert2';
import IsMountedWrapper from '../../utils/functions/isMountedWrapper';
import { BookingStore } from '../../utils/stores/BookingStore';
import { ProductsStore } from '../../utils/stores/ProductsStore';
import { gradients } from '../../utils/theme';
import BookingFormSummary from './BookingFormSummary';
import BookingFormSummaryAccordion from './BookingFormSummaryAccordion';
import LargeStepper from './LargeStepper';
import CheckAvailability from './formSections/AvailabilitySections';
import CustomerDetails from './formSections/CustomerDetails';
import ExtraSelectionStep from './formSections/ExtraSelectionStep';
import ParticipantsInfoStep from './formSections/ParticipantInfoFields';
import Payments from './formSections/PaymentSection';
import PickUpSection from './formSections/PickUpSection';
import StepperButtons from './formSections/StepperButtons';

// Sits above just about everything (incl. third-party chat widgets) so the
// modal controls are never blocked.
const BOOKING_MODAL_Z_INDEX = 9999999999;

const bookingFormSx = {
    bookingContent: {
        padding: { xs: '15px', sm: '0 32px 32px 32px' },
        maxHeight: { xs: 'none', sm: '90vh' },
        minHeight: 600,
        width: '100%',
        display: 'flex',
        justifyContent: 'space-between',
        flexDirection: 'column',
        overflowY: 'visible',
    },
    dialogTitle: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        padding: { xs: '10px 10px 10px 22px', sm: '20px 20px 20px 32px' },
    },
    titleText: {
        WebkitBackgroundClip: 'text',
        color: 'transparent',
        backgroundImage: gradients.circular,
    },
    contentAndSummary: {
        flexDirection: { xs: 'row', md: 'row-reverse' },
    },
    dialog: {
        zIndex: BOOKING_MODAL_Z_INDEX,
        '& .MuiDialog-container': {
            width: '100%',
            borderRadius: '8px',
        },
        '& .MuiDialog-paper': {
            maxHeight: '90vh',
            '@media (max-width:599.95px)': {
                margin: 0,
                width: '100%',
                maxWidth: '100%',
                maxHeight: '100%',
                borderRadius: 0,
            },
        },
        background:
            'linear-gradient(180deg, rgba(255,255,255,0) 0%, rgba(252,252,252,1) 10%, rgba(248,248,248,1) 88%, rgba(248,248,248,1) 100%)',
    },
} as const;

interface BookingModalProps {
    /** Home-page variant: prepend a Tour selector to the availability box. */
    readonly showTourSelect?: boolean;
}

export default function BookingModal({ showTourSelect }: BookingModalProps = {}) {
    const bookingController = useContext(BookingStore);
    const productController = useContext(ProductsStore);
    const [open, setOpen] = useState(false);
    const theme = useTheme();
    const bookingStep = bookingController.step;
    const fullScreen = useMediaQuery(theme.breakpoints.down('sm'));
    const isMounted = IsMountedWrapper();
    const navigate = useNavigate();
    const hasExtras = productController?.product?.extras.length > 0;
    const localBookingString = import.meta.env.VITE_LOCAL_BOOKING;
    useScript({ src: 'https://secure.ewaypayments.com/scripts/eCrypt.min.js' });

    useEffect(() => {
        if (bookingController.booking?.bookingResponse?.success) {
            const ref = bookingController.booking.bookingResponse.refNum;
            bookingController.completedBookings.setData(
                {
                    booking: bookingController.booking,
                    package: bookingController.booking?.isPackage && bookingController.packageBooking.data,
                },
                isMounted
            );
            localStorage.setItem(
                localBookingString,
                JSON.stringify({
                    booking: bookingController.booking,
                    package: bookingController.booking?.isPackage && bookingController.packageBooking.data,
                })
            );
            setOpen(false);
            navigate(`/confirmation/${ref}`);
        }
    }, [bookingController.booking.bookingResponse, navigate, bookingController, isMounted, localBookingString]);

    const swalAlert = (text: string) => {
        Swal.fire({
            icon: 'error',
            title: 'Oops...',
            iconColor: theme.palette.primary.main,
            text: text,
            confirmButtonColor: theme.palette.primary.main,
        });
    };

    const handleClickOpen = () => {
        TagManager.dataLayer({
            dataLayer: {
                event: 'book_now_click',
                booking_source: showTourSelect ? 'landing_page' : 'product_page',
                product_id: productController?.product?.productCode,
                product_name: productController?.product?.name,
                page_path: window.location.pathname,
            },
        });
        if (showTourSelect && !productController?.product?.productCode) {
            swalAlert('Please select a tour to continue.');
            return;
        }
        const sumOfFares = sum(bookingController.booking.quantities?.map((x) => x.value));
        const errorType = {
            time: !bookingController.booking?.tour?.startTimeLocal,
            seats:
                sumOfFares >
                ((productController?.availability?.sessions || []).find((session) =>
                    moment(session?.startTime).isSame(bookingController.booking?.tour?.startTimeLocal)
                )?.seatsRemaining || 0),
            fares: sum(map(bookingController.booking.quantities, (x) => x.value)) === 0,
        };
        bookingController.reset();
        Object.values(errorType).includes(true)
            ? swalAlert('Please make sure you have selected a time for your tour, and a valid booking/fare option')
            : setOpen(true);
    };

    const handleClose = () => {
        setOpen(false);
        bookingController.reset();
    };

    const DialogTitle = (props: any) => {
        const { onClose, children } = props;
        return (
            <MuiDialogTitle sx={bookingFormSx.dialogTitle}>
                <Typography variant="h5" component="span" sx={bookingFormSx.titleText}>
                    {children}
                </Typography>
                <IconButton aria-label="close" onClick={onClose}>
                    <CloseIcon style={{ color: 'rgb(0 0 0 / 23%)' }} />
                </IconButton>
            </MuiDialogTitle>
        );
    };

    const getStepContent = () => {
        if (hasExtras) {
            switch (bookingStep) {
                case 0:
                    return <CustomerDetails />;
                case 1:
                    return <ParticipantsInfoStep />;
                case 2:
                    return <ExtraSelectionStep />;
                case 3:
                    return <PickUpSection />;
                case 4:
                    return <Payments />;
            }
        }
        switch (bookingStep) {
            case 0:
                return <CustomerDetails />;
            case 1:
                return <ParticipantsInfoStep />;
            case 2:
                return <PickUpSection />;
            case 3:
                return <Payments />;
        }
    };

    return (
        <>
            <CheckAvailability handleClickOpen={handleClickOpen} showTourSelect={showTourSelect} />
            <Dialog
                open={open}
                aria-label="booking-form"
                maxWidth="lg"
                fullScreen={fullScreen}
                scroll="body"
                sx={bookingFormSx.dialog}
            >
                <DialogTitle onClose={handleClose}>Booking</DialogTitle>
                <DialogContent sx={bookingFormSx.bookingContent}>
                    <LargeStepper numberOfSteps={hasExtras ? 5 : 4} />
                    <Hidden lgUp>
                        <BookingFormSummaryAccordion />
                    </Hidden>
                    <Grid container sx={bookingFormSx.contentAndSummary}>
                        <Grid size={{ xs: 12, lg: 4 }}>
                            <Hidden mdDown>
                                <BookingFormSummary />
                            </Hidden>
                        </Grid>
                        <Grid size={{ xs: 12, lg: 8 }}>{getStepContent()}</Grid>
                        <Grid size={{ xs: 12 }}>
                            <div style={{ display: bookingController.booking.bookingComplete ? 'none' : 'unset' }}>
                                <StepperButtons numberOfSteps={hasExtras ? 5 : 4} />
                            </div>
                        </Grid>
                    </Grid>
                </DialogContent>
            </Dialog>
        </>
    );
}

/* Surcharge Calcs:

let surcharge = (subtotals*.015).toFixed(2);
let total = (subtotals + Number(surcharge));
set(bookingData, 'subtotals', subtotals)
set(bookingData, 'surcharge', surcharge);
set(bookingData, 'totalAmount', total);

*/
